'use client'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'

interface HeroEntity {
  id: string
  name: string
  imageUrl: string
}

interface TileState {
  current: HeroEntity
  next: HeroEntity | null
  showNext: boolean
}

interface HeroBackgroundProps {
  pool: HeroEntity[]
  intervalMs?: number
  fadeMs?: number
  tileCount?: number
  className?: string
}

function pickDeterministic(pool: HeroEntity[], tileCount: number): TileState[] {
  if (pool.length === 0) return []
  return Array.from({ length: tileCount }, (_, i) => ({
    current: pool[i % pool.length],
    next: null,
    showNext: false,
  }))
}

function pickShuffled(pool: HeroEntity[], tileCount: number): TileState[] {
  const shuffled = [...pool].sort(() => Math.random() - 0.5)
  return Array.from({ length: tileCount }, (_, i) => ({
    current: shuffled[i % shuffled.length],
    next: null,
    showNext: false,
  }))
}

export function HeroBackground({
  pool,
  intervalMs = 3500,
  fadeMs = 4000,
  tileCount = 8,
  className = 'absolute inset-0 grid grid-cols-2 grid-rows-4 sm:grid-cols-4 sm:grid-rows-2 gap-2 p-2',
}: HeroBackgroundProps) {
  // Deterministic first paint so SSR HTML matches client hydration.
  const [tiles, setTiles] = useState<TileState[]>(() => pickDeterministic(pool, tileCount))
  const tilesRef = useRef(tiles)
  tilesRef.current = tiles

  useEffect(() => {
    if (pool.length === 0) return
    setTiles(pickShuffled(pool, tileCount))
  }, [pool, tileCount])

  useEffect(() => {
    if (pool.length === 0) return
    let timer: ReturnType<typeof setTimeout> | null = null
    let cancelled = false

    const scheduleNext = () => {
      timer = setTimeout(() => {
        if (cancelled) return
        const tileIdx = Math.floor(Math.random() * tileCount)
        const currentIds = new Set(tilesRef.current.map((t) => t.current.id))
        const candidates = pool.filter((p) => !currentIds.has(p.id))
        const incoming =
          candidates.length > 0
            ? candidates[Math.floor(Math.random() * candidates.length)]
            : pool[Math.floor(Math.random() * pool.length)]

        setTiles((prev) =>
          prev.map((t, i) =>
            i === tileIdx ? { ...t, next: incoming, showNext: false } : t
          )
        )

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (cancelled) return
            setTiles((prev) =>
              prev.map((t, i) => (i === tileIdx ? { ...t, showNext: true } : t))
            )
          })
        })

        setTimeout(() => {
          if (cancelled) return
          setTiles((prev) =>
            prev.map((t, i) =>
              i === tileIdx
                ? { current: incoming, next: null, showNext: false }
                : t
            )
          )
          scheduleNext()
        }, fadeMs + 50)
      }, intervalMs)
    }

    scheduleNext()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [pool, intervalMs, fadeMs, tileCount])

  return (
    <div className={className}>
      {tiles.map((tile, idx) => (
        <div key={idx} className="relative overflow-hidden rounded bg-[#0f2654]">
          <Image
            src={tile.current.imageUrl}
            alt=""
            fill
            sizes="(max-width: 640px) 50vw, 25vw"
            className="object-cover"
            unoptimized
          />
          {tile.next && (
            <Image
              src={tile.next.imageUrl}
              alt=""
              fill
              sizes="(max-width: 640px) 50vw, 25vw"
              className="object-cover"
              style={{
                opacity: tile.showNext ? 1 : 0,
                transition: `opacity ${fadeMs}ms ease-in-out`,
              }}
              unoptimized
            />
          )}
        </div>
      ))}
    </div>
  )
}
